import { useQueryClient } from "@tanstack/react-query";

import { AppModal } from "@krea/shared/design-system/modal";

import { queryKeyMap } from "@/features/query-client";
import { NotificationsSection } from "@/features/users/components/notification-settings-section";
import { ProfileSection } from "@/features/users/components/profile-section";
import { useUpdateUserMutation } from "@/features/users/hooks/use-update-user.mutation";
import { useUpdateUserCompanySettingsMutation } from "@/features/users/hooks/use-update-user-company-settings.mutation";
import type { ILenderUser } from "@/features/users/types";

import { useGetCurrentLenderCompanyQuery } from "../../hooks/use-get-lender-company.query";

interface IEditTeamMemberModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  user: ILenderUser;
}

export const EditTeamMemberModal = ({
  isOpen,
  setIsOpen,
  user,
}: IEditTeamMemberModalProps) => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutateAsync: updateUser } = useUpdateUserMutation();
  const { mutate: updateUserCompanySettings } =
    useUpdateUserCompanySettingsMutation();
  const queryClient = useQueryClient();

  return (
    <AppModal
      open={isOpen}
      setOpen={setIsOpen}
      title="Edit Team Member"
      lockOutsideInteraction={false}
    >
      <div className="space-y-10 overflow-y-auto">
        <ProfileSection
          user={user}
          onClose={() => setIsOpen(false)}
          onUpdateUser={async (data) => {
            await updateUser({ id: user.id, variables: data });

            if (!lenderCompany?.id) return;

            await queryClient.invalidateQueries({
              queryKey: queryKeyMap.getLenderCompanyUsers(lenderCompany.id),
            });
            setIsOpen(false);
          }}
        />

        {user.settings && (
          <NotificationsSection
            settings={user.settings}
            onUpdateUserSettings={(data) => {
              if (!lenderCompany?.id) return;

              updateUserCompanySettings(
                {
                  lenderCompanyId: lenderCompany.id,
                  userId: user.id,
                  data,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: queryKeyMap.getLenderCompanyUsers(
                        lenderCompany.id,
                      ),
                    });
                  },
                },
              );
            }}
          />
        )}
      </div>
    </AppModal>
  );
};
